import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function IndividualResults({ user }) {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [submittingAll, setSubmittingAll] = useState({});
  
  // Classroom modal states
  const [showClassroomModal, setShowClassroomModal] = useState(false);
  const [selectedQuizForClassroom, setSelectedQuizForClassroom] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedCoursework, setSelectedCoursework] = useState('');
  const [availableCourseworks, setAvailableCourseworks] = useState([]);
  const [loadingCourseworks, setLoadingCourseworks] = useState(false);
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const [submittingGrades, setSubmittingGrades] = useState(false);
  
  // Edit score modal states
  const [showEditScoreModal, setShowEditScoreModal] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [newScore, setNewScore] = useState('');
  const [scoreReason, setScoreReason] = useState('');

  useEffect(() => {
    fetchResults();
    loadCourses();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/teacher/${user.id}/individual-results`);
      setResults(response.data.results);
    } catch (error) {
      console.error('Error fetching results:', error);
      alert('Error al cargar los resultados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitToClassroom = async (sessionId) => {
    if (!window.confirm('¿Enviar esta calificación a Google Classroom?')) return;

    setSubmitting({ ...submitting, [sessionId]: true });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/teacher/submit-individual-grade`, {
        teacher_id: user.id,
        session_id: sessionId
      });

      alert(`Calificación enviada: ${response.data.grade}/100 para ${response.data.student_email}`);
      
      // Refresh results
      fetchResults();
    } catch (error) {
      console.error('Error submitting grade:', error);
      alert(error.response?.data?.detail || 'Error al enviar calificación');
    } finally {
      setSubmitting({ ...submitting, [sessionId]: false });
    }
  };

  const handleSubmitAllForQuiz = async (quizId, quizTitle) => {
    if (!window.confirm(`¿Enviar TODAS las calificaciones pendientes de "${quizTitle}" a Google Classroom?`)) return;

    setSubmittingAll({ ...submittingAll, [quizId]: true });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/teacher/submit-all-grades-for-quiz`, {
        teacher_id: user.id,
        quiz_id: quizId
      });

      let message = response.data.message;
      if (response.data.errors && response.data.errors.length > 0) {
        message += '\n\nErrores:\n' + response.data.errors.slice(0, 5).join('\n');
        if (response.data.errors.length > 5) {
          message += `\n... y ${response.data.errors.length - 5} más`;
        }
      }

      alert(message);
      
      // Refresh results
      fetchResults();
    } catch (error) {
      console.error('Error submitting all grades:', error);
      alert(error.response?.data?.detail || 'Error al enviar calificaciones');
    } finally {
      setSubmittingAll({ ...submittingAll, [quizId]: false });
    }
  };

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadCourseworks = async (courseId) => {
    setLoadingCourseworks(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/course/${courseId}/courseworks/${user.id}`);
      setAvailableCourseworks(response.data.courseworks || []);
    } catch (error) {
      console.error('Error loading courseworks:', error);
      setAvailableCourseworks([]);
    } finally {
      setLoadingCourseworks(false);
    }
  };

  const handleImportToClassroom = (quizId, quizTitle) => {
    setSelectedQuizForClassroom({ id: quizId, title: quizTitle });
    setShowClassroomModal(true);
    setSelectedCourse('');
    setSelectedCoursework('');
    setAvailableCourseworks([]);
  };

  const handleCreateClassroomAssignment = async () => {
    if (!selectedCourse || !selectedQuizForClassroom) {
      alert('Por favor selecciona una clase');
      return;
    }

    setCreatingAssignment(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/teacher/quiz/${selectedQuizForClassroom.id}/create-classroom-assignment`,
        {
          teacher_id: user.id,
          course_id: selectedCourse
        }
      );

      setSelectedCoursework(response.data.coursework_id);
      alert(`✅ Tarea creada en Classroom: ${response.data.title}`);
    } catch (error) {
      console.error('Error creating assignment:', error);
      alert('Error al crear la tarea en Classroom: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingAssignment(false);
    }
  };

  const handleSubmitAllGradesFromModal = async () => {
    if (!selectedCourse || !selectedCoursework || !selectedQuizForClassroom) {
      alert('Por favor crea primero la tarea en Classroom o selecciona una existente');
      return;
    }

    const quizResults = results.filter(r => r.quiz_id === selectedQuizForClassroom.id);
    
    if (!window.confirm(`¿Enviar las ${quizResults.length} calificaciones a Google Classroom?`)) {
      return;
    }

    setSubmittingGrades(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/teacher/quiz/${selectedQuizForClassroom.id}/submit-all-grades`,
        {
          teacher_id: user.id,
          course_id: selectedCourse,
          coursework_id: selectedCoursework
        }
      );

      alert(`✅ ${response.data.message}\n${response.data.errors && response.data.errors.length > 0 ? 'Errores:\n' + response.data.errors.join('\n') : ''}`);
      setShowClassroomModal(false);
      fetchResults(); // Reload to show updated status
    } catch (error) {
      console.error('Error submitting grades:', error);
      alert('Error al enviar calificaciones: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmittingGrades(false);
    }
  };

  const handleEditScore = (result) => {
    setEditingResult(result);
    setNewScore(result.score);
    setScoreReason('');
    setShowEditScoreModal(true);
  };

  const handleSaveScore = async () => {
    if (!newScore || newScore === '') {
      alert('Por favor ingresa una calificación válida');
      return;
    }

    if (!scoreReason.trim()) {
      alert('Por favor indica el motivo de la modificación');
      return;
    }

    const scoreNum = parseFloat(newScore);
    if (isNaN(scoreNum) || scoreNum < 0) {
      alert('La calificación debe ser un número positivo');
      return;
    }

    try {
      await axios.patch(`${BACKEND_URL}/api/quiz/result/${editingResult.result_id}/score`, {
        new_score: scoreNum,
        reason: scoreReason,
        teacher_id: user.id
      });

      alert('✅ Calificación actualizada correctamente');
      setShowEditScoreModal(false);
      fetchResults(); // Reload results
    } catch (error) {
      console.error('Error updating score:', error);
      alert('Error al actualizar la calificación: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteQuizResults = async (quizId, quizTitle) => {
    if (!window.confirm(`¿Eliminar TODOS los resultados de "${quizTitle}"?\n\n⚠️ Esta acción no se puede deshacer.`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/teacher/quiz/${quizId}/results`, {
        data: { teacher_id: user.id }
      });

      alert(`Resultados de "${quizTitle}" eliminados correctamente`);
      
      // Refresh results
      fetchResults();
    } catch (error) {
      console.error('Error deleting results:', error);
      alert(error.response?.data?.detail || 'Error al eliminar resultados');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Cargando resultados...</p>
        </div>
      </div>
    );
  }

  // Group results by quiz
  const resultsByQuiz = results.reduce((acc, result) => {
    if (!acc[result.quiz_id]) {
      acc[result.quiz_id] = {
        quiz_title: result.quiz_title,
        results: []
      };
    }
    acc[result.quiz_id].results.push(result);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-primary hover:text-purple-700 mb-2 flex items-center gap-2"
              >
                ← Volver al Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Resultados de Quizzes Individuales</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {results.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No hay resultados de quizzes individuales
            </h3>
            <p className="text-gray-500">Los estudiantes aún no han completado quizzes individuales</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(resultsByQuiz).map(([quizId, data]) => {
              const pendingCount = data.results.filter(r => r.classroom_coursework_id && !r.submitted_to_classroom).length;
              const hasClassroomLinked = data.results.some(r => r.classroom_coursework_id);
              
              return (
                <div key={quizId} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-primary text-white px-6 py-4">
                    <div className="flex justify-between items-center flex-wrap gap-3">
                      <div>
                        <h2 className="text-xl font-bold">{data.quiz_title}</h2>
                        <p className="text-sm opacity-90">
                          {data.results.length} estudiante(s)
                          {hasClassroomLinked && (
                            <span className="ml-2 px-2 py-1 bg-white/20 rounded text-xs">
                              📚 Vinculado a Classroom
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {!hasClassroomLinked ? (
                          <button
                            onClick={() => handleImportToClassroom(quizId, data.quiz_title)}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition flex items-center gap-2"
                            title="Importar este quiz a Google Classroom"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Importar a Classroom
                          </button>
                        ) : (
                          pendingCount > 0 && (
                            <button
                              onClick={() => handleSubmitAllForQuiz(quizId, data.quiz_title)}
                              disabled={submittingAll[quizId]}
                              className="px-4 py-2 bg-white text-primary rounded-lg font-semibold hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {submittingAll[quizId] ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                                  Enviando...
                                </>
                              ) : (
                                <>
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Enviar Todas ({pendingCount})
                                </>
                              )}
                            </button>
                          )
                        )}
                        <button
                          onClick={() => handleDeleteQuizResults(quizId, data.quiz_title)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition flex items-center gap-2"
                          title="Eliminar todos los resultados de este quiz"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Limpiar
                        </button>
                      </div>
                    </div>
                  </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estudiante
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Puntos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Calificación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Classroom
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acción
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.results.map((result) => (
                        <tr key={result.session_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{result.student_name}</div>
                            <div className="text-sm text-gray-500">{result.student_email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-primary">{result.score}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-green-600">{result.grade_percentage}/100</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {result.completed_at ? new Date(result.completed_at).toLocaleDateString('es-ES') : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {result.classroom_coursework_id ? (
                              result.submitted_to_classroom ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  ✓ Enviado
                                </span>
                              ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  Pendiente
                                </span>
                              )
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                Sin vincular
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditScore(result)}
                                className="px-3 py-1 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition text-xs"
                                title="Editar calificación"
                              >
                                ✏️ Editar
                              </button>
                              {result.classroom_coursework_id && !result.submitted_to_classroom ? (
                                <button
                                  onClick={() => handleSubmitToClassroom(result.session_id)}
                                  disabled={submitting[result.session_id]}
                                  className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                >
                                  {submitting[result.session_id] ? 'Enviando...' : 'Enviar'}
                                </button>
                              ) : result.classroom_coursework_id && result.submitted_to_classroom ? (
                                <span className="text-green-600 font-semibold text-xs">✓ Enviado</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>

      {/* Classroom Export Modal */}
      {showClassroomModal && selectedQuizForClassroom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">📤 Exportar a Google Classroom</h3>
            <p className="text-sm text-gray-600 mb-4">Quiz: <strong>{selectedQuizForClassroom.title}</strong></p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Selecciona la clase</label>
              <select
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setSelectedCoursework('');
                  setAvailableCourseworks([]);
                  if (e.target.value) {
                    loadCourseworks(e.target.value);
                  }
                }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Selecciona una clase --</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name} {course.section && `(${course.section})`}
                  </option>
                ))}
              </select>
            </div>

            {selectedCourse && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Opción de Tarea</label>
                  
                  <div className="space-y-3">
                    {/* Crear nueva */}
                    <button
                      onClick={handleCreateClassroomAssignment}
                      disabled={!selectedCourse || creatingAssignment}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    >
                      {creatingAssignment ? 'Creando...' : '✨ Crear Nueva Tarea en Classroom'}
                    </button>
                    
                    {/* Seleccionar existente */}
                    <div className="border-t pt-3">
                      <p className="text-sm font-medium mb-2">O selecciona una tarea existente:</p>
                      {loadingCourseworks ? (
                        <p className="text-sm text-gray-600">Cargando tareas...</p>
                      ) : availableCourseworks.length > 0 ? (
                        <select
                          value={selectedCoursework}
                          onChange={(e) => setSelectedCoursework(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- Selecciona una tarea existente --</option>
                          {availableCourseworks.map((cw) => (
                            <option key={cw.id} value={cw.id}>
                              {cw.title} ({cw.maxPoints} pts) - {cw.state}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-sm text-gray-500">No hay tareas en esta clase</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {selectedCoursework && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">✅ Tarea seleccionada correctamente</p>
              </div>
            )}

            <div className="border-t pt-4 mb-4">
              <button
                onClick={handleSubmitAllGradesFromModal}
                disabled={!selectedCourse || !selectedCoursework || submittingGrades}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingGrades ? 'Enviando...' : `📊 Enviar Calificaciones`}
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowClassroomModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Score Modal */}
      {showEditScoreModal && editingResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              ✏️ Editar Calificación
            </h2>
            
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                <strong>Estudiante:</strong> {editingResult.student_name}
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Quiz:</strong> {editingResult.quiz_title}
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Calificación Actual:</strong> 
                <span className="text-blue-600 font-bold ml-2">{editingResult.score}</span> puntos
                <span className="text-gray-500 ml-2">({editingResult.grade_percentage}/100)</span>
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nueva Calificación (puntos)
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={newScore}
                onChange={(e) => setNewScore(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ingresa la nueva calificación"
              />
              <p className="text-xs text-gray-500 mt-1">
                La calificación se calculará automáticamente como porcentaje
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo de la Modificación <span className="text-red-500">*</span>
              </label>
              <textarea
                value={scoreReason}
                onChange={(e) => setScoreReason(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Error en corrección, Penalización por copia, Bonus por esfuerzo, etc."
              />
              <p className="text-xs text-gray-500 mt-1">
                Este motivo quedará registrado en el historial
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowEditScoreModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveScore}
                disabled={!scoreReason.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IndividualResults;
